import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Provider } from "react-redux";

// Redux
import { store } from "./store";
import { getAuth } from "./slices/auth";
import setAuthToken from "./utils/auth";
import Landing from "./components/landing/Landing";
import Features from "./components/features/Features";
import Proposition from "./components/proposition/Proposition";
import Pricing from "./components/pricing/Pricing";
import FAQs from "./components/faqs/FAQs";
import Login from "./components/auth/Login";
import Feed from "./components/feed/Feed";
import ActionableRequestsProvider from "./contexts/ActionableRequestsProvider";
import PrivateRoute from "./components/routing/PrivateRoute";
import RoomsOverview from "./components/rooms/RoomsOverview";
import RoomsTemplates from "./components/rooms/RoomsTemplates";
import Devices from "./components/devices/Devices";
import Analytics from "./components/insights/Analytics";
import Log from "./components/insights/Log";
import Announcements from "./components/communication/Announcements";
import ContactBooks from "./components/communication/ContactBooks";
import NotFound from "./components/routing/NotFound";
import Calling from "./components/communication/Calling";
// import Reciprocate from "./components/communication/Reciprocate";
import Campaigns from "./components/content/Campaigns";
import PhotoAlbums from "./components/content/PhotoAlbums";
import Events from "./components/content/Events";
import Services from "./components/content/Services";
import Menus from "./components/content/Menus";
import Newsletters from "./components/content/Newsletters";
import Weblinks from "./components/content/Weblinks";
import Trivia from "./components/content/Trivia";
import Surveys from "./components/surveys/Surveys";
import PointsOfInterest from "./components/orientation/PointsOfInterest";
import Directions from "./components/orientation/Directions";
import Vicinity from "./components/orientation/Vicinity";
import Configuration from "./components/integration/Configuration";
import History from "./components/integration/History";
import Appliances from "./components/smart_control/Appliances";
import Groups from "./components/smart_control/Groups";
import Catalog from "./components/property/Catalog";
import Availability from "./components/property/Availability";
import Profile from "./components/settings/Profile";
import Items from "./components/payment/Items";
import RequestEmailNotification from "./components/confirmation/RequestEmailNotification";
import PaymentConfirmation from "./components/confirmation/PaymentConfirmation";
import PaymentCancellation from "./components/confirmation/PaymentCancellation";
import Manage from "./components/tasks/Manage";
import Logs from "./components/tasks/Logs";
import Overview from "./components/oversight/Overview";
import OversightFeed from "./components/oversight/Feed";
import OversightCommunication from "./components/oversight/Communication";
import OversightDevices from "./components/oversight/Devices";
import OversightContent from "./components/oversight/Content";
import OversightLog from "./components/oversight/Log";
import OversightOrientation from "./components/oversight/Orientation";
import OversightIntegration from "./components/oversight/Integration";
import OversightSmartControl from "./components/oversight/SmartControl";
import OversightTasks from "./components/oversight/Tasks";
import OversightUsers from "./components/oversight/Users";
import Prompts from "./components/third_party/Prompts";
import Payment2FA from "./components/confirmation/Payment2FA";
// import ThreeDSecureConfirmation from "./components/confirmation/3DSecureConfirmation";

if (localStorage.token) {
  setAuthToken(localStorage.token);
}

function App() {
  useEffect(() => {
    store.dispatch(getAuth());
  }, []);

  return (
    <Provider store={store}>
      <ActionableRequestsProvider>
        <Router>
          <Routes>
            {/* Landing - Public */}
            <Route path="/" element={<Landing />} />

            {/* Features - Public */}
            <Route path="/features" element={<Features />} />

            {/* Proposition - Public */}
            <Route path="/proposition" element={<Proposition />} />

            {/* Pricing - Public */}
            <Route path="/pricing" element={<Pricing />} />

            {/* FAQs - Public */}
            <Route path="/faqs" element={<FAQs />} />

            {/* Login - Public */}
            <Route path="/login" element={<Login />} />

            {/* Feed - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Feed - Full app access",
                    "Runner",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/feed" element={<Feed />} />
            </Route>

            {/* Rooms (Overview) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Rooms - Full app access",
                    "Runner",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/rooms/overview" element={<RoomsOverview />} />
            </Route>

            {/* Rooms (Templates) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Rooms - Full app access",
                    "Request templates - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/rooms/templates" element={<RoomsTemplates />} />
            </Route>

            {/* Devices - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Devices - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/devices" element={<Devices />} />
            </Route>

            {/* Insights (Analytics) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Insights - Full app access",
                    "Family and friends",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/insights/analytics" element={<Analytics />} />
            </Route>

            {/* Insights (Log) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Insights - Full app access",
                    "Family and friends",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/insights/log" element={<Log />} />
            </Route>

            {/* Communication (Announcements) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Communication - Full app access",
                    "Family and friends",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/communication/announcements"
                element={<Announcements />}
              />
            </Route>

            {/* Communication (Contact books) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Communication - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/communication/contact-books"
                element={<ContactBooks />}
              />
            </Route>

            {/* Communication (Calling) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Communication - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/communication/calling" element={<Calling />} />
            </Route>

            {/* Communication (Reciprocate) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Communication - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/communication/reciprocation"
                element={<NotFound />}
              />
            </Route>

            {/* Content (Campaigns) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/campaigns" element={<Campaigns />} />
            </Route>

            {/* Content (Photo albums) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "Family and friends",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/photo-albums" element={<PhotoAlbums />} />
            </Route>

            {/* Content (Events) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/events" element={<Events />} />
            </Route>

            {/* Content (Services) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/services" element={<Services />} />
            </Route>

            {/* Content (Menus) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/menus" element={<Menus />} />
            </Route>

            {/* Content (Newsletters) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/newsletters" element={<Newsletters />} />
            </Route>

            {/* Content (Weblinks) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/weblinks" element={<Weblinks />} />
            </Route>

            {/* Content (Trivia) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/content/trivia" element={<Trivia />} />
            </Route>

            {/* Surveys - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/surveys" element={<Surveys />} />
            </Route>

            {/* Orientation (Points of interest) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/orientation/points-of-interest"
                element={<PointsOfInterest />}
              />
            </Route>

            {/* Orientation (Directions) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/orientation/directions" element={<Directions />} />
            </Route>

            {/* Orientation (Vicinity) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Content - Full app access",
                    "Marketing",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/orientation/vicinity" element={<Vicinity />} />
            </Route>

            {/* Integration (Webhooks) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Integration - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/integration/configuration"
                element={<Configuration />}
              />
            </Route>

            {/* Integration (History) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Integration - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/integration/history" element={<History />} />
            </Route>

            {/* Smart control (Appliances) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Smart control - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route
                path="/smart-control/appliances"
                element={<Appliances />}
              />
            </Route>

            {/* Smart control (Groups) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Smart control - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/smart-control/groups" element={<Groups />} />
            </Route>

            {/* Payment (Items) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Payment - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/payment/items" element={<Items />} />
            </Route>

            {/* Property (Catalog) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Property - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/property/catalog" element={<Catalog />} />
            </Route>

            {/* Property (Availability) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Property - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/property/availability" element={<Availability />} />
            </Route>

            {/* Settings (Profile) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Settings - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/settings/profile" element={<Profile />} />
            </Route>

            {/* Tasks (Manage) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Tasks - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/tasks/manage" element={<Manage />} />
            </Route>

            {/* Tasks (Logs) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Tasks - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/tasks/logs" element={<Logs />} />
            </Route>

            {/* Third party (Prompts) - Private - Admin */}
            <Route
              element={
                <PrivateRoute
                  allowedGroups={[
                    "Admin",
                    "Third party - Full app access",
                    "audico staff",
                  ]}
                />
              }
            >
              <Route path="/third-party/prompts" element={<Prompts />} />
            </Route>

            {/* Oversight (Overview) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/overview" element={<Overview />} />
            </Route>

            {/* Oversight (Feed) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/feed" element={<OversightFeed />} />
            </Route>

            {/* Oversight (Communication) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route
                path="/oversight/communication"
                element={<OversightCommunication />}
              />
            </Route>

            {/* Oversight (Devices) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/devices" element={<OversightDevices />} />
            </Route>

            {/* Oversight (Content) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/content" element={<OversightContent />} />
            </Route>

            {/* Oversight (Log) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/log" element={<OversightLog />} />
            </Route>

            {/* Oversight (Orientation) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route
                path="/oversight/orientation"
                element={<OversightOrientation />}
              />
            </Route>

            {/* Oversight (Integration) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route
                path="/oversight/integration"
                element={<OversightIntegration />}
              />
            </Route>

            {/* Oversight (Smart Control) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route
                path="/oversight/smart-control"
                element={<OversightSmartControl />}
              />
            </Route>

            {/* Oversight (Tasks) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/tasks" element={<OversightTasks />} />
            </Route>

            {/* Oversight (Users) - Private - Admin */}
            <Route element={<PrivateRoute allowedGroups={["Executive"]} />}>
              <Route path="/oversight/users" element={<OversightUsers />} />
            </Route>

            {/* Confirmation (Request email notification) - Public - Admin */}
            <Route
              path="/email/:status/:request_id/:property_id"
              element={<RequestEmailNotification />}
            />

            {/* Confirmation (Payment confirmation) - Public - Admin */}
            <Route
              path="/payment/confirm/:transaction_id/:property_id"
              element={<PaymentConfirmation />}
            />

            {/* Confirmation (Payment confirmation) - Public - Admin */}
            <Route
              path="/payment/cancel/:transaction_id/:property_id"
              element={<PaymentCancellation />}
            />

            {/* Confirmation (Payment 2FA) - Public - Admin */}
            <Route path="/2fa-payment" element={<Payment2FA />} />

            {/* <Route
              path="/payment/opayo-portal/notification/:property_id/:transaction_id"
              element={<ThreeDSecureConfirmation />}
            /> */}

            {/* Not Found */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate replace to="/404" />} />
          </Routes>
        </Router>
      </ActionableRequestsProvider>
    </Provider>
  );
}

export default App;
